/**
 * Widget API Client
 */

import type { InitResponse, MessageResponse, Conversation } from './types';
import * as store from './store';
import { emitWidgetAnalytics, isPaymentLink } from './analytics';

let apiUrl = '';
let projectId = '';
let eventSource: EventSource | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let connectionStableTimer: ReturnType<typeof setTimeout> | null = null;
let responseWaitTimer: ReturnType<typeof setTimeout> | null = null;
let responsePollTimer: ReturnType<typeof setInterval> | null = null;
let connectionGeneration = 0;
let visibilityListenerInstalled = false;
const SSE_STABLE_AFTER_MS = 60_000;
const RESPONSE_WAIT_TIMEOUT_MS = 2 * 60_000;

export function configure(url: string, pId: string) {
  apiUrl = url.replace(/\/$/, '');
  projectId = pId;
}

function messageAnalyticsBase(extra: Record<string, string | number | boolean | null | undefined> = {}) {
  return {
    project_id: projectId,
    visitor_id: store.visitorId.value,
    conversation_id: store.conversationId.value,
    ...extra,
  };
}

function beginAwaitingResponse(): void {
  store.setAwaitingResponse(true);
  if (responseWaitTimer) clearTimeout(responseWaitTimer);
  if (responsePollTimer) clearInterval(responsePollTimer);
  const conversationId = store.conversationId.value;
  let polling = false;
  // A draining server can still hold SSE while a new server saves the reply.
  // Reuse the persisted-message reader only while waiting for an answer.
  responsePollTimer = setInterval(async () => {
    if (polling || !conversationId || store.conversationId.value !== conversationId || document.hidden) return;
    polling = true;
    try { await catchUpMessages(conversationId, 'response_catchup'); }
    finally { polling = false; }
  }, 5_000);
  responseWaitTimer = setTimeout(() => {
    responseWaitTimer = null;
    finishAwaitingResponse();
  }, RESPONSE_WAIT_TIMEOUT_MS);
}

function finishAwaitingResponse(): void {
  store.setAwaitingResponse(false);
  if (responsePollTimer) {
    clearInterval(responsePollTimer);
    responsePollTimer = null;
  }
  if (responseWaitTimer) {
    clearTimeout(responseWaitTimer);
    responseWaitTimer = null;
  }
  if (!store.isOpen.value) disconnect();
}

/**
 * Initialize widget session
 */
export async function init(): Promise<InitResponse> {
  const session = store.loadSession();

  // Capture browser timezone for customer local time awareness
  let browserTimezone: string | undefined;
  try {
    browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch { /* older browsers — skip */ }

  const requestBody = {
    visitorToken: session.token || undefined,
    pageUrl: window.location.href,
    referrer: document.referrer || undefined,
    userAgent: navigator.userAgent,
    timezone: browserTimezone,
  };

  let response: Response;
  try {
    response = await fetch(`${apiUrl}/api/widget/${projectId}/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(session.token ? { 'X-Visitor-Token': session.token } : {}),
      },
      body: JSON.stringify(requestBody),
    });
  } catch (error) {
    emitWidgetAnalytics('chuck_session_failed', {
      project_id: projectId,
      reason: 'init_network_error',
    });
    throw error;
  }

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[ChuckWidget] Init failed:', response.status, errorText);
    emitWidgetAnalytics('chuck_session_failed', {
      project_id: projectId,
      status_code: response.status,
      reason: 'init_response_not_ok',
    });
    throw new Error(`Failed to initialize widget: ${response.status}`);
  }

  const data: InitResponse = await response.json();

  // Store session
  store.setSession(data.visitorToken, data.visitorId, data.conversationId);
  store.setConfig(data.config);
  store.setMessages(data.messages);
  store.setLoading(false);

  emitWidgetAnalytics('chuck_session_initialized', {
    project_id: projectId,
    visitor_id: data.visitorId,
    conversation_id: data.conversationId,
    existing_message_count: data.messages.length,
    resumed_session: Boolean(session.token),
  });

  installVisibilityListener();
  if (shouldMaintainRealtime()) {
    connectSSE(data.conversationId);
  }

  return data;
}

/**
 * Send message with optimistic UI (message appears instantly)
 */
export async function sendMessage(content: string, contentType: 'text' | 'image' = 'text'): Promise<MessageResponse> {
  const vToken = store.visitorToken.value;
  const vId = store.visitorId.value;
  const cId = store.conversationId.value;

  if (!vToken || !vId || !cId) {
    throw new Error('Session not initialized');
  }

  // Add message optimistically - it appears INSTANTLY
  const tempId = store.addOptimisticMessage(content, contentType);
  let failureTracked = false;
  beginAwaitingResponse();

  try {
    const response = await fetch(`${apiUrl}/api/widget/${projectId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Visitor-Token': vToken,
      },
      body: JSON.stringify({
        conversationId: cId,
        visitorId: vId,
        visitorToken: vToken,
        content,
        contentType,
      }),
    });

    if (!response.ok) {
      // Remove optimistic message on failure
      store.removeOptimisticMessage(tempId);
      emitWidgetAnalytics('chuck_message_failed', messageAnalyticsBase({
        content_type: contentType,
        status_code: response.status,
        reason: 'message_response_not_ok',
      }));
      failureTracked = true;
      throw new Error('Failed to send message');
    }

    const data: MessageResponse = await response.json();

    // Update optimistic message with real data (real ID, etc.)
    store.updateOptimisticMessage(tempId, data.message);

    // If there's an AI response, add it too (SSE might also add it, dedupe handles it)
    if (data.aiResponse) {
      store.addMessage(data.aiResponse);
      finishAwaitingResponse();
      emitWidgetAnalytics('chuck_ai_response_received', messageAnalyticsBase({
        delivery: 'http_response',
        message_id: data.aiResponse.id,
        has_payment_link: isPaymentLink(data.aiResponse.content),
      }));
      if (isPaymentLink(data.aiResponse.content)) {
        emitWidgetAnalytics('chuck_payment_link_shown', messageAnalyticsBase({
          delivery: 'http_response',
          message_id: data.aiResponse.id,
        }));
      }
    }

    // Update human handling state
    store.setHumanHandling(data.humanHandling);
    if (data.humanHandling) finishAwaitingResponse();

    emitWidgetAnalytics('chuck_message_sent', messageAnalyticsBase({
      content_type: contentType,
      message_id: data.message.id,
      human_handling: data.humanHandling,
    }));

    return data;
  } catch (error) {
    finishAwaitingResponse();
    // Remove optimistic message on error
    store.removeOptimisticMessage(tempId);
    if (!failureTracked) {
      emitWidgetAnalytics('chuck_message_failed', messageAnalyticsBase({
        content_type: contentType,
        reason: 'message_exception',
      }));
    }
    throw error;
  }
}

/**
 * Send image message with optional text caption (optimistic UI)
 * If caption is provided, they are sent as ONE combined message
 */
export async function sendImageWithCaption(imageDataUrl: string, caption?: string): Promise<MessageResponse> {
  const vToken = store.visitorToken.value;
  const vId = store.visitorId.value;
  const cId = store.conversationId.value;

  if (!vToken || !vId || !cId) {
    throw new Error('Session not initialized');
  }

  // Add message optimistically - shows the local image preview instantly
  const tempId = store.addOptimisticMessage(caption || '', 'image', imageDataUrl);
  let failureTracked = false;
  beginAwaitingResponse();

  try {
    const response = await fetch(`${apiUrl}/api/widget/${projectId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Visitor-Token': vToken,
      },
      body: JSON.stringify({
        conversationId: cId,
        visitorId: vId,
        visitorToken: vToken,
        content: imageDataUrl,
        contentType: 'image',
        caption: caption || undefined, // Text to accompany the image
      }),
    });

    if (!response.ok) {
      store.removeOptimisticMessage(tempId);
      emitWidgetAnalytics('chuck_message_failed', messageAnalyticsBase({
        content_type: 'image',
        has_caption: Boolean(caption),
        status_code: response.status,
        reason: 'message_response_not_ok',
      }));
      failureTracked = true;
      throw new Error('Failed to send message');
    }

    const data: MessageResponse = await response.json();

    // Update optimistic message with real data
    // For images, use the uploaded URL from server (if available)
    const realMessage = {
      ...data.message,
      // Keep local image for display if server doesn't return URL yet
      content: data.message.content?.startsWith('http') ? data.message.content : imageDataUrl,
    };
    store.updateOptimisticMessage(tempId, realMessage);

    // If there's an AI response, add it too
    if (data.aiResponse) {
      store.addMessage(data.aiResponse);
      finishAwaitingResponse();
      emitWidgetAnalytics('chuck_ai_response_received', messageAnalyticsBase({
        delivery: 'http_response',
        message_id: data.aiResponse.id,
        has_payment_link: isPaymentLink(data.aiResponse.content),
      }));
      if (isPaymentLink(data.aiResponse.content)) {
        emitWidgetAnalytics('chuck_payment_link_shown', messageAnalyticsBase({
          delivery: 'http_response',
          message_id: data.aiResponse.id,
        }));
      }
    }

    // Update human handling state
    store.setHumanHandling(data.humanHandling);
    if (data.humanHandling) finishAwaitingResponse();

    emitWidgetAnalytics('chuck_message_sent', messageAnalyticsBase({
      content_type: 'image',
      has_caption: Boolean(caption),
      message_id: data.message.id,
      human_handling: data.humanHandling,
    }));

    return data;
  } catch (error) {
    finishAwaitingResponse();
    store.removeOptimisticMessage(tempId);
    if (!failureTracked) {
      emitWidgetAnalytics('chuck_message_failed', messageAnalyticsBase({
        content_type: 'image',
        has_caption: Boolean(caption),
        reason: 'message_exception',
      }));
    }
    throw error;
  }
}

/**
 * Send image message (without caption)
 * @deprecated Use sendImageWithCaption for combined image+text
 */
export async function sendImage(imageDataUrl: string): Promise<MessageResponse> {
  return sendImageWithCaption(imageDataUrl);
}

/**
 * Send a conversation starter message (no visitor bubble shown)
 * The prompt goes to AI but isn't displayed in chat
 */
export async function sendStarterMessage(prompt: string): Promise<MessageResponse> {
  const vToken = store.visitorToken.value;
  const vId = store.visitorId.value;
  const cId = store.conversationId.value;

  if (!vToken || !vId || !cId) {
    throw new Error('Session not initialized');
  }

  // Show typing indicator immediately (no visitor bubble)
  store.setTyping(true, 'Chuck');
  let failureTracked = false;
  beginAwaitingResponse();

  try {
    const response = await fetch(`${apiUrl}/api/widget/${projectId}/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Visitor-Token': vToken,
      },
      body: JSON.stringify({
        conversationId: cId,
        visitorId: vId,
        visitorToken: vToken,
        content: prompt,
        contentType: 'text',
        isStarter: true,
      }),
    });

    if (!response.ok) {
      emitWidgetAnalytics('chuck_message_failed', messageAnalyticsBase({
        content_type: 'starter',
        status_code: response.status,
        reason: 'message_response_not_ok',
      }));
      failureTracked = true;
      throw new Error('Failed to send message');
    }

    const data: MessageResponse = await response.json();

    // If there's an AI response, add it (SSE might also add it, dedupe handles it)
    if (data.aiResponse) {
      store.addMessage(data.aiResponse);
      finishAwaitingResponse();
      emitWidgetAnalytics('chuck_ai_response_received', messageAnalyticsBase({
        delivery: 'http_response',
        message_id: data.aiResponse.id,
        has_payment_link: isPaymentLink(data.aiResponse.content),
      }));
      if (isPaymentLink(data.aiResponse.content)) {
        emitWidgetAnalytics('chuck_payment_link_shown', messageAnalyticsBase({
          delivery: 'http_response',
          message_id: data.aiResponse.id,
        }));
      }
    }

    store.setHumanHandling(data.humanHandling);
    if (data.humanHandling) finishAwaitingResponse();

    emitWidgetAnalytics('chuck_message_sent', messageAnalyticsBase({
      content_type: 'starter',
      message_id: data.message.id,
      human_handling: data.humanHandling,
    }));

    return data;
  } catch (error) {
    finishAwaitingResponse();
    store.setTyping(false);
    if (!failureTracked) {
      emitWidgetAnalytics('chuck_message_failed', messageAnalyticsBase({
        content_type: 'starter',
        reason: 'message_exception',
      }));
    }
    throw error;
  }
}

/**
 * Request human takeover
 */
export async function requestHuman(): Promise<void> {
  const cId = store.conversationId.value;

  if (!cId) {
    throw new Error('Session not initialized');
  }

  const response = await fetch(`${apiUrl}/api/widget/${projectId}/request-human`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(store.visitorToken.value ? { 'X-Visitor-Token': store.visitorToken.value } : {}),
    },
    body: JSON.stringify({
      conversationId: cId,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to request human');
  }

  emitWidgetAnalytics('chuck_human_transfer_requested', messageAnalyticsBase({
    requested_by: 'visitor',
  }));
}

/**
 * Mark messages as read
 */
export async function markRead(): Promise<void> {
  const cId = store.conversationId.value;
  const vId = store.visitorId.value;

  if (!cId || !vId) {
    return;
  }

  await fetch(`${apiUrl}/api/widget/${projectId}/mark-read`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(store.visitorToken.value ? { 'X-Visitor-Token': store.visitorToken.value } : {}),
    },
    body: JSON.stringify({
      conversationId: cId,
      visitorId: vId,
    }),
  });
}

/**
 * Get visitor's conversations
 */
export async function getConversations(): Promise<Conversation[]> {
  const vId = store.visitorId.value;
  const vToken = store.visitorToken.value;

  if (!vId || !vToken) {
    return [];
  }

  const response = await fetch(
    `${apiUrl}/api/widget/${projectId}/conversations?visitorId=${vId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Visitor-Token': vToken,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch conversations');
  }

  const data = await response.json();
  return data.conversations;
}

/**
 * Start a new conversation
 */
export async function startNewConversation(): Promise<void> {
  const vId = store.visitorId.value;
  const vToken = store.visitorToken.value;

  if (!vId || !vToken) {
    throw new Error('Session not initialized');
  }

  const response = await fetch(`${apiUrl}/api/widget/${projectId}/conversations/new`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Visitor-Token': vToken,
    },
    body: JSON.stringify({
      visitorId: vId,
      visitorToken: vToken,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to start new conversation');
  }

  const data = await response.json();

  // Update store with new conversation
  store.setConversationId(data.conversationId);
  store.setMessages([]);
  store.setHumanHandling(false);

  emitWidgetAnalytics('chuck_conversation_started', {
    project_id: projectId,
    visitor_id: vId,
    conversation_id: data.conversationId,
  });

  // Reconnect SSE to new conversation when the chat is actively visible.
  disconnect();
  await setRealtimeActive(true);
}

/**
 * Switch to a different conversation
 */
export async function switchConversation(conversationId: string): Promise<void> {
  const vId = store.visitorId.value;
  const vToken = store.visitorToken.value;

  if (!vId || !vToken) {
    throw new Error('Session not initialized');
  }

  const response = await fetch(
    `${apiUrl}/api/widget/${projectId}/conversations/${conversationId}?visitorId=${vId}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-Visitor-Token': vToken,
      },
    }
  );

  if (!response.ok) {
    throw new Error('Failed to switch conversation');
  }

  const data = await response.json();

  // Update store with conversation data
  store.setConversationId(conversationId);
  store.setMessages(data.messages);
  store.setHumanHandling(data.humanHandling || false, data.humanHandlerName || undefined);

  emitWidgetAnalytics('chuck_conversation_switched', {
    project_id: projectId,
    visitor_id: vId,
    conversation_id: conversationId,
    existing_message_count: data.messages?.length || 0,
    human_handling: Boolean(data.humanHandling),
  });

  // Reconnect SSE to this conversation when the chat is actively visible.
  disconnect();
  await setRealtimeActive(true);
}

/**
 * Connect to SSE stream for real-time updates
 */
let sseReconnectAttempts = 0;

function shouldMaintainRealtime(): boolean {
  return (store.isOpen.value || store.awaitingResponse.value)
    && (typeof document === 'undefined' || !document.hidden)
    && Boolean(store.conversationId.value);
}

async function catchUpMessages(conversationId: string, delivery = 'reconnect_catchup'): Promise<void> {
  const lastMessage = store.messages.value[store.messages.value.length - 1];
  const params = new URLSearchParams({ conversationId });
  if (lastMessage?.createdAt) params.set('since', lastMessage.createdAt);

  try {
    const response = await fetch(`${apiUrl}/api/widget/${projectId}/messages?${params}`, {
      headers: {
        ...(store.visitorToken.value ? { 'X-Visitor-Token': store.visitorToken.value } : {}),
      },
    });
    if (!response.ok) return;

    const data = await response.json();
    if (store.conversationId.value !== conversationId) return;
    for (const message of data.messages || []) {
      const alreadyPresent = store.messages.value.some((existing) => existing.id === message.id);
      if (alreadyPresent) continue;

      store.addMessage(message);
      if (message.senderType !== 'visitor') {
        finishAwaitingResponse();
        emitWidgetAnalytics('chuck_ai_response_received', messageAnalyticsBase({
          delivery,
          message_id: message.id,
          sender_type: message.senderType,
          content_type: message.contentType,
          has_payment_link: isPaymentLink(message.content),
        }));
        if (isPaymentLink(message.content)) {
          emitWidgetAnalytics('chuck_payment_link_shown', messageAnalyticsBase({
            delivery,
            message_id: message.id,
          }));
        }
      }
    }
  } catch {
    // The next reconnect or normal response path can catch up again.
  }
}

/**
 * A fresh signed link for one picture whose link has expired, read through the visitor's own messages
 * endpoint and stored on the message. Returns the new link, or null when it could not be had.
 */
export async function refreshPictureLink(messageId: string): Promise<string | null> {
  const conversationId = store.conversationId.value;
  const token = store.visitorToken.value;
  const message = store.messages.value.find((m) => m.id === messageId);
  if (!conversationId || !token || !message?.createdAt) return null;

  // Ask from just before the picture's own time, so the answer includes it however old it is.
  const since = new Date(Date.parse(message.createdAt) - 1).toISOString();
  try {
    const response = await fetch(
      `${apiUrl}/api/widget/${projectId}/messages?${new URLSearchParams({ conversationId, since })}`,
      { headers: { 'X-Visitor-Token': token } }
    );
    if (!response.ok) return null;

    const data = await response.json();
    const fresh = (data.messages || []).find((m: { id: string }) => m.id === messageId);
    if (store.conversationId.value !== conversationId || fresh?.contentType !== 'image' || !fresh.content) return null;

    store.updateMessageContent(messageId, fresh.content);
    return fresh.content;
  } catch {
    return null;
  }
}

export async function setRealtimeActive(active: boolean): Promise<void> {
  if (!active || !shouldMaintainRealtime()) {
    disconnect();
    return;
  }

  const conversationId = store.conversationId.value;
  if (!conversationId || eventSource || reconnectTimer) return;

  await catchUpMessages(conversationId);
  if (shouldMaintainRealtime() && store.conversationId.value === conversationId) {
    connectSSE(conversationId);
  }
}

function connectSSE(conversationId: string) {
  if (!shouldMaintainRealtime() || store.conversationId.value !== conversationId) return;

  const generation = ++connectionGeneration;

  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }

  // Close existing connection
  if (eventSource) {
    eventSource.close();
  }

  const visitorToken = store.visitorToken.value;
  if (!visitorToken) return;

  const params = new URLSearchParams({ conversationId, visitorToken });
  const url = `${apiUrl}/api/widget/${projectId}/stream?${params}`;
  const source = new EventSource(url);
  eventSource = source;

  const isCurrentConnection = () => (
    generation === connectionGeneration && eventSource === source
  );

  source.onopen = () => {
    if (!isCurrentConnection()) return;
    store.setConnected(true);
    if (connectionStableTimer) clearTimeout(connectionStableTimer);
    connectionStableTimer = setTimeout(() => {
      connectionStableTimer = null;
      if (isCurrentConnection()) sseReconnectAttempts = 0;
    }, SSE_STABLE_AFTER_MS);
    emitWidgetAnalytics('chuck_sse_connected', {
      project_id: projectId,
      conversation_id: conversationId,
    });
  };

  source.onerror = () => {
    if (!isCurrentConnection()) return;
    store.setConnected(false);
    if (connectionStableTimer) {
      clearTimeout(connectionStableTimer);
      connectionStableTimer = null;
    }

    // Close only this generation. A stale callback must never close a newer stream.
    source.close();
    eventSource = null;

    if (!shouldMaintainRealtime()) return;

    // Exponential backoff: 5s, 10s, 20s, 40s, max 60s
    sseReconnectAttempts++;
    const delay = Math.min(5000 * Math.pow(2, sseReconnectAttempts - 1), 60000);
    emitWidgetAnalytics('chuck_sse_error', {
      project_id: projectId,
      conversation_id: conversationId,
      reconnect_attempt: sseReconnectAttempts,
      reconnect_delay_ms: delay,
    });

    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (
        generation === connectionGeneration &&
        shouldMaintainRealtime() &&
        store.conversationId.value === conversationId
      ) {
        void catchUpMessages(conversationId).finally(() => {
          if (
            generation === connectionGeneration &&
            shouldMaintainRealtime() &&
            store.conversationId.value === conversationId
          ) {
            connectSSE(conversationId);
          }
        });
      }
    }, delay);
  };

  // Handle message events
  source.addEventListener('message', (event) => {
    if (!isCurrentConnection()) return;
    try {
      const data = JSON.parse(event.data);
      const msg = data.message;

      if (!msg) return;

      // Skip visitor's own messages - they're already shown via optimistic UI
      // This prevents duplicate messages from SSE broadcast
      if (msg.senderType === 'visitor') {
        return;
      }

      // Don't add duplicate messages (by ID)
      const existing = store.messages.value.find((m) => m.id === msg.id);
      if (existing) {
        return;
      }

      // Clear transfer countdown when follow-up messages arrive from agent
      if (msg.senderType === 'agent' && store.transferCountdown.value > 0) {
        store.transferCountdown.value = 0;
      }

      // For image messages, transform to use imageUrl as content for display
      if (msg.contentType === 'image' && msg.imageUrl) {
        store.addMessage({
          ...msg,
          content: msg.imageUrl,
          caption: msg.caption || msg.content,
        });
      } else {
        store.addMessage(msg);
      }
      finishAwaitingResponse();
      emitWidgetAnalytics('chuck_ai_response_received', {
        project_id: projectId,
        conversation_id: conversationId,
        visitor_id: store.visitorId.value,
        delivery: 'sse',
        message_id: msg.id,
        sender_type: msg.senderType,
        content_type: msg.contentType,
        has_payment_link: isPaymentLink(msg.content),
      });
      if (isPaymentLink(msg.content)) {
        emitWidgetAnalytics('chuck_payment_link_shown', {
          project_id: projectId,
          conversation_id: conversationId,
          visitor_id: store.visitorId.value,
          delivery: 'sse',
          message_id: msg.id,
        });
      }
      playNotificationSound();
      updateTabTitle();
    } catch (e) {
      console.error('Failed to parse message event:', e);
    }
  });

  // Handle typing events
  source.addEventListener('typing', (event) => {
    if (!isCurrentConnection()) return;
    try {
      const data = JSON.parse(event.data);
      store.setTyping(data.isTyping, data.senderName);
    } catch (e) {
      console.error('Failed to parse typing event:', e);
    }
  });

  // Handle human takeover events
  source.addEventListener('human_takeover', (event) => {
    if (!isCurrentConnection()) return;
    try {
      const data = JSON.parse(event.data);
      store.setHumanHandling(true, data.handlerName || 'Agent');
      emitWidgetAnalytics('chuck_human_transfer_requested', {
        project_id: projectId,
        conversation_id: conversationId,
        visitor_id: store.visitorId.value,
        requested_by: 'agent',
        handler_present: true,
      });

      // Add system message
      store.addMessage({
        id: `system-${Date.now()}`,
        content: `${data.handlerName} has joined the conversation.`,
        contentType: 'system',
        senderType: 'system',
        senderName: null,
        isAiResponse: false,
        createdAt: new Date().toISOString(),
      });
    } catch (e) {
      console.error('Failed to parse human_takeover event:', e);
    }
  });

  // Handle human returned events
  source.addEventListener('human_returned', () => {
    if (!isCurrentConnection()) return;
    store.setHumanHandling(false);

    // Add system message
    store.addMessage({
      id: `system-${Date.now()}`,
      content: 'Chuck is now handling this conversation.',
      contentType: 'system',
      senderType: 'system',
      senderName: null,
      isAiResponse: false,
      createdAt: new Date().toISOString(),
    });
  });

  // Handle status events
  source.addEventListener('status', (event) => {
    if (!isCurrentConnection()) return;
    try {
      const data = JSON.parse(event.data);
      if (data.humanHandling !== undefined) {
        store.setHumanHandling(data.humanHandling);
        if (data.humanHandling) {
          emitWidgetAnalytics('chuck_human_transfer_requested', {
            project_id: projectId,
            conversation_id: conversationId,
            visitor_id: store.visitorId.value,
            requested_by: 'ai',
            status: data.status,
          });
        }
      }
    } catch (e) {
      console.error('Failed to parse status event:', e);
    }
  });

  // Handle connected event
  source.addEventListener('connected', () => {
    if (!isCurrentConnection()) return;
    store.setConnected(true);
  });
}

/**
 * Disconnect SSE
 */
export function disconnect() {
  connectionGeneration++;
  sseReconnectAttempts = 0;
  if (connectionStableTimer) {
    clearTimeout(connectionStableTimer);
    connectionStableTimer = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
  store.setConnected(false);
}

function handleVisibilityChange() {
  if (document.hidden) {
    disconnect();
    return;
  }

  if (store.isOpen.value) {
    void setRealtimeActive(true);
    store.unreadCount.value = 0;
    resetTabTitle();
    markRead().catch(() => {});
  }
}

function installVisibilityListener() {
  if (typeof document === 'undefined' || visibilityListenerInstalled) return;
  document.addEventListener('visibilitychange', handleVisibilityChange);
  visibilityListenerInstalled = true;
}

export function resetRuntime() {
  disconnect();
  sseReconnectAttempts = 0;
  finishAwaitingResponse();
  if (typeof document !== 'undefined' && visibilityListenerInstalled) {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    visibilityListenerInstalled = false;
  }
}

/**
 * Play notification sound
 * Plays when widget is closed OR when the browser tab is not focused
 */
let audioContext: AudioContext | null = null;

function playNotificationSound() {
  if (!store.serverConfig.value?.soundEnabled) {
    return;
  }

  // Play sound if widget is closed OR tab is not focused
  const tabHidden = typeof document !== 'undefined' && document.hidden;
  if (store.isOpen.value && !tabHidden) {
    return; // Widget is open AND tab is focused - no sound needed
  }

  try {
    if (!audioContext) {
      audioContext = new AudioContext();
    }

    // Resume context if suspended (required after user interaction policy)
    if (audioContext.state === 'suspended') {
      audioContext.resume();
    }

    const now = audioContext.currentTime;

    // Two-tone notification: higher pitch, more noticeable
    // First tone: 880Hz
    const osc1 = audioContext.createOscillator();
    const gain1 = audioContext.createGain();
    osc1.connect(gain1);
    gain1.connect(audioContext.destination);
    osc1.frequency.value = 880;
    osc1.type = 'sine';
    gain1.gain.setValueAtTime(0.4, now);
    gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.15);
    osc1.start(now);
    osc1.stop(now + 0.15);

    // Second tone: 1046Hz (after short pause)
    const osc2 = audioContext.createOscillator();
    const gain2 = audioContext.createGain();
    osc2.connect(gain2);
    gain2.connect(audioContext.destination);
    osc2.frequency.value = 1046;
    osc2.type = 'sine';
    gain2.gain.setValueAtTime(0.4, now + 0.18);
    gain2.gain.exponentialRampToValueAtTime(0.01, now + 0.4);
    osc2.start(now + 0.18);
    osc2.stop(now + 0.4);
  } catch (e) {
    // Audio not supported
  }
}

/**
 * Tab title management for unread message notifications
 */
let originalTitle: string | null = null;

function updateTabTitle() {
  if (typeof document === 'undefined') return;

  // Store original title once
  if (originalTitle === null) {
    originalTitle = document.title;
  }

  const count = store.unreadCount.value;
  if (count > 0) {
    document.title = `(${count}) ${originalTitle}`;
  } else if (originalTitle) {
    document.title = originalTitle;
  }
}

export function resetTabTitle() {
  if (typeof document === 'undefined' || originalTitle === null) return;
  document.title = originalTitle;
}
